/**
 * 格式化 API 错误信息。
 * 兼容 FastAPI 校验数组、HTTPException detail 对象，以及 axios error 包装对象。
 */

type ErrorLikeRecord = Record<string, unknown>;

function isRecord(value: unknown): value is ErrorLikeRecord {
  return typeof value === 'object' && value !== null;
}

function dedupe(parts: string[]): string[] {
  return Array.from(new Set(parts.map(part => part.trim()).filter(Boolean)));
}

function extractMessageParts(value: unknown): string[] {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return [String(value)];
  }

  if (Array.isArray(value)) {
    return dedupe(value.flatMap(item => extractMessageParts(item)));
  }

  if (!isRecord(value)) {
    return [];
  }

  const directKeys = ['msg', 'message', 'error', 'reason', 'description'] as const;
  for (const key of directKeys) {
    const parts = extractMessageParts(value[key]);
    if (parts.length > 0) {
      return parts;
    }
  }

  const nestedKeys = ['detail', 'errors', 'non_field_errors'] as const;
  for (const key of nestedKeys) {
    const parts = extractMessageParts(value[key]);
    if (parts.length > 0) {
      return parts;
    }
  }

  if (Array.isArray(value.fields) && value.fields.length > 0) {
    return dedupe((value.fields as unknown[]).flatMap(field => extractMessageParts(field)));
  }

  return [];
}

function resolveErrorMessage(source: unknown, fallback: string): string {
  const candidates: unknown[] = [];

  if (isRecord(source)) {
    candidates.push(source.response);

    if (isRecord(source.response)) {
      candidates.push(source.response.data);

      if (isRecord(source.response.data)) {
        candidates.push(source.response.data.detail);
      }
    }

    candidates.push(source.detail);
    candidates.push(source.message);
  }

  candidates.push(source);

  for (const candidate of candidates) {
    const parts = extractMessageParts(candidate);
    if (parts.length > 0) {
      return parts.join('; ');
    }
  }

  return fallback;
}

export function formatErrorMessage(error: unknown, fallback: string = '操作失败'): string {
  return resolveErrorMessage(error, fallback);
}

/**
 * 安全地格式化任意可能是对象的错误消息。
 * 用于直接从 detail 字段提取错误信息。
 */
export function safeErrorMessage(detail: unknown, fallback: string = '操作失败'): string {
  return resolveErrorMessage(detail, fallback);
}
